import { TextField } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { type HTMLAttributes, type Key } from 'react';
import useIsViewport from 'hooks/useIsViewport';
import { DisabledFieldTooltip } from '../DisabledFieldTooltip';
import { getFieldControlSx } from '../fieldStyles';
import type { FieldRendererProps } from '../types';

export function AutocompleteField({
  field,
  className,
  sx,
  hasWhiteFields = false,
  isDisabled,
  isReadOnly,
  autoFocus,
  onChange,
}: FieldRendererProps) {
  const isMobile = useIsViewport('md');
  const selectedOption = field.selectOptions.find((o) => o.value === field.value) ?? null;
  const displayLabel = selectedOption?.label ?? '';

  const endAdornment =
    isMobile && isDisabled ? <DisabledFieldTooltip variant="adornment" title={displayLabel} /> : undefined;

  const autocomplete = (
    <Autocomplete
      className={className}
      sx={getFieldControlSx(hasWhiteFields, sx)}
      readOnly={isReadOnly}
      id={field.id.toString()}
      options={field.selectOptions}
      getOptionLabel={(o) => String(o?.label ?? '')}
      isOptionEqualToValue={(o, v) => {
        return o.value === v.value;
      }}
      filterOptions={(options, { inputValue }) => {
        const q = inputValue.trim().toLowerCase().replace(/\s+/g, ' ');
        if (!q) return options;
        return options.filter((o) =>
          String(o?.label ?? '')
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .includes(q),
        );
      }}
      disabled={isDisabled}
      value={selectedOption}
      onChange={(_, newSelected) => onChange(newSelected?.value ?? '')}
      renderOption={(props, option, { index }) => {
        const { key: _muiKey, ...rest } = props as { key?: Key } & HTMLAttributes<HTMLLIElement>;
        return (
          <li key={`${option.value}-${index}`} {...rest}>
            {option.label}
          </li>
        );
      }}
      renderInput={(params: any) => (
        <TextField
          {...params}
          variant="outlined"
          margin="dense"
          autoFocus={autoFocus}
          fullWidth
          label={field.label}
          required={field.required}
          slotProps={{
            ...params.slotProps,

            input: endAdornment
              ? {
                  ...params.slotProps.input,
                  endAdornment: (
                    <>
                      {endAdornment}
                      {params.slotProps.input?.endAdornment}
                    </>
                  ),
                }
              : params.slotProps.input,
          }}
        />
      )}
    />
  );

  if (!isMobile && isDisabled) {
    return (
      <DisabledFieldTooltip variant="desktop" title={displayLabel}>
        {autocomplete}
      </DisabledFieldTooltip>
    );
  }

  return autocomplete;
}
